import React from "react";
import { Menu, MenuButton, MenuItem, MenuList } from "@reach/menu-button";
import { useMachine } from "@xstate/react";
import { cx } from "@nju/css";
import {
  BlockContent,
  BlockText,
  GeneralErrorNotification,
  Heading,
  Loader,
  Pagination,
  Panel,
  Separator,
  Text,
} from "@nju/ui";
import { OrdersTable } from "../order";
import { dispositionsMachine, ISortOrder } from "./dispositions.machine";
import { Filters } from "./Filters";

const sortLabelMap: Record<ISortOrder, string> = {
  "-orderDate": "daty zamówienia - od najnowszych",
  orderDate: "daty zamówienia - od najstarszych",
  "-appointmentStartDate": "daty instalacji - od najnowszych",
  appointmentStartDate: "daty instalacji - od najstarszych",
} as const;

export function DispositionsPage() {
  const [current, send] = useMachine(dispositionsMachine);
  const { orders, perPage, total, page, sort } = current.context;

  const handleOnNext = React.useCallback(() => {
    send({ type: "SET_CURRENT_PAGE", value: page + 1 });
  }, [page, send]);

  const handleOnPrev = React.useCallback(() => {
    send({ type: "SET_CURRENT_PAGE", value: page - 1 });
  }, [page, send]);

  const handleOnSelect = React.useCallback(
    (page: number) => {
      send({ type: "SET_CURRENT_PAGE", value: page });
    },
    [send],
  );

  const handleSetPaymentFilter = React.useCallback(
    (items: string[]) => {
      send({ type: "SET_FILTER_PAYMENT", value: items });
    },
    [send],
  );

  const handleSetStatusFilter = React.useCallback(
    (items: string[]) => {
      send({ type: "SET_FILTER_STATUS", value: items });
    },
    [send],
  );

  const handleSetOfferTypeFilter = React.useCallback(
    (items: string[]) => {
      send({ type: "SET_FILTER_OFFER_TYPE", value: items });
    },
    [send],
  );

  const handleSetCancellationReasonFilter = React.useCallback(
    (items: string[]) => {
      send({ type: "SET_FILTER_CANCELLATION_REASON", value: items });
    },
    [send],
  );

  return (
    <div>
      <Heading
        font="ttfirs"
        level="h2"
        tone="neutral"
        variant="dashboardTitle"
        weight="bold"
      >
        Twoje dyspozycje sprzedażowe
      </Heading>
      <Separator size="s" tone="invisible" />
      <div className="flex gap-4 flex-col from-tablet:flex-row">
        <Filters
          onCancellationReasonChange={handleSetCancellationReasonFilter}
          onOfferTypeChange={handleSetOfferTypeFilter}
          onOrderStatusChange={handleSetStatusFilter}
          onPaymentStatusChange={handleSetPaymentFilter}
        />
      </div>
      <Separator size="s" tone="invisible" />
      <div className="flex flex-col from-tablet:flex-row from-tablet:justify-between">
        <BlockText>
          liczba zamówień: <Text weight="bold">{total}</Text>
        </BlockText>
        <div>
          <Text>sortuj: </Text>
          <Menu>
            <MenuButton className="border-0 cursor-pointer underline text-left from-tablet:text-center">
              {sortLabelMap[sort]}
            </MenuButton>
            <MenuList className="bg-white radius-[8px] mt-2 z-[3] relative shadow-[0_20px_40px_0_hsl(0,0%,0%,20%)]">
              <MenuItem
                className={cx("flex cursor-pointer items-center p-3 gap-2", {
                  "bg-ripeLemon": sort === "-orderDate",
                })}
                onSelect={() => {
                  send({ type: "SET_SORT_ORDER", value: "-orderDate" });
                }}
              >
                <Text withUnderline={true}>{sortLabelMap["-orderDate"]}</Text>
              </MenuItem>
              <MenuItem
                className={cx("flex cursor-pointer items-center p-3 gap-2", {
                  "bg-ripeLemon": sort === "orderDate",
                })}
                onSelect={() => {
                  send({ type: "SET_SORT_ORDER", value: "orderDate" });
                }}
              >
                <Text withUnderline={true}>{sortLabelMap.orderDate}</Text>
              </MenuItem>
              <MenuItem
                className={cx("flex cursor-pointer items-center p-3 gap-2", {
                  "bg-ripeLemon": sort === "-appointmentStartDate",
                })}
                onSelect={() => {
                  send({
                    type: "SET_SORT_ORDER",
                    value: "-appointmentStartDate",
                  });
                }}
              >
                <Text withUnderline={true}>
                  {sortLabelMap["-appointmentStartDate"]}
                </Text>
              </MenuItem>
              <MenuItem
                className={cx("flex cursor-pointer items-center p-3 gap-2", {
                  "bg-ripeLemon": sort === "appointmentStartDate",
                })}
                onSelect={() => {
                  send({
                    type: "SET_SORT_ORDER",
                    value: "appointmentStartDate",
                  });
                }}
              >
                <Text withUnderline={true}>
                  {sortLabelMap.appointmentStartDate}
                </Text>
              </MenuItem>
            </MenuList>
          </Menu>
        </div>
      </div>
      <Separator size="xs" tone="invisible" />
      {current.matches("loading") && (
        <BlockContent
          border="primary"
          rounded="primary"
          spacing="l"
          tone="secondary"
        >
          <div className="flex justify-center">
            <Loader size="50px" />
          </div>
        </BlockContent>
      )}
      {current.matches("ready") && orders.length > 0 && (
        <OrdersTable orders={orders} />
      )}
      {current.matches("ready") && orders.length === 0 && (
        <div className="flex justify-center">
          <Panel
            title="Brak wyników spełniających podane kryteria"
            width="full"
          >
            Zmień wartości filtrów aby wyświetlić pasujące zamówienia.
          </Panel>
        </div>
      )}
      {current.matches("error") && (
        <GeneralErrorNotification
          callback={() => {
            window.location.reload();
          }}
        />
      )}
      <Separator size="s" tone="invisible" />
      <div>
        <Pagination
          currentPage={page + 1}
          offset={2}
          onNext={handleOnNext}
          onPrev={handleOnPrev}
          onSelect={handleOnSelect}
          pages={total > 0 ? Math.ceil(total / perPage) : 0}
        />
      </div>
    </div>
  );
}
